import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Alert } from 'flowbite-react';
import { HiExclamationCircle, HiOutlineArrowLeft, HiOutlineMail } from 'react-icons/hi';
import { useAuth } from '../contexts/AuthContext';

interface LocationState {
  message?: string;
  suggestion?: string;
}

export const AccessDeniedPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, authError } = useAuth();

  const state = location.state as LocationState | null;

  // Determine the error message
  let errorMessage = state?.message || 'You do not have permission to access this resource.';
  let suggestion = state?.suggestion || '';

  // Check for domain_not_allowed error from auth context
  if (authError === 'domain_not_allowed') {
    errorMessage = 'Your organization is not registered for this directory.';
    suggestion = 'If you believe this is an error, please contact AIU support to have your organization added.';
  }

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
      <div className="max-w-lg w-full">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
          {/* Icon */}
          <div className="flex justify-center mb-6">
            <div className="bg-red-100 dark:bg-red-900 rounded-full p-4">
              <HiExclamationCircle className="h-12 w-12 text-red-600 dark:text-red-400" />
            </div>
          </div>

          {/* Title */}
          <h1 className="text-2xl font-bold text-center text-gray-900 dark:text-white mb-4">
            Access Denied
          </h1>

          {/* Error Alert */}
          <Alert color="failure" className="mb-6">
            <span className="font-medium">Error:</span> {errorMessage}
          </Alert>

          {/* Suggestion */}
          {suggestion && (
            <p className="text-gray-600 dark:text-gray-400 text-center mb-6">
              {suggestion}
            </p>
          )}

          {/* Help Information */}
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-6">
            <h3 className="font-medium text-gray-900 dark:text-white mb-2">
              Need Help?
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              If you are a staff member at an AIU member district and need access to this directory, please contact:
            </p>
            <a
              href="mailto:servicedesk@aiu3.net"
              className="flex items-center gap-2 text-blue-600 dark:text-blue-400 hover:underline text-sm"
            >
              <HiOutlineMail className="h-4 w-4" />
              servicedesk@aiu3.net
            </a>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleGoBack}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
            >
              <HiOutlineArrowLeft className="h-4 w-4" />
              Go Back
            </button>
            <button
              onClick={handleLogout}
              className="flex-1 px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
            >
              Sign In with Different Account
            </button>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-6">
          AIU Directory Database
        </p>
      </div>
    </div>
  );
};

export default AccessDeniedPage;
